import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable";
import {RequestParam} from "@/shared/api";
import {DriveLogTable} from "./driveLogTable";
import {RecordDetailsDialog, RecordDetailsDialogConfig} from "@/shared/recording/recordDetailsDialog";
import {RecordingTable} from "@/shared/recording/recordingTable";
import {htmlEscape} from "@/shared/utils/string";
import {formatDuration} from "@/shared/utils/datefunctions";
import {GPSPosition, ReportDriveLog} from "@/shared/timr";
import {Platform, RecordedEntryStatus} from "@/shared/timrTypes";

export class DriveLogDetailsDialog extends RecordDetailsDialog<ReportDriveLog> {

	protected driveLogRevisionTable: DriveLogDetailsDialogRevisionTable;

	constructor(config: RecordDetailsDialogConfig<ReportDriveLog>) {
		super(config);
	}

	public show(driveLog: ReportDriveLog) {
		this.recordWithCustomFieldValues = driveLog.purpose !== "" || driveLog.visited !== "" || driveLog.route !== "";

		this.setDriveLogFields(driveLog);
		this.loadRevisionTable(driveLog);
		super.show(driveLog);

		this.toggleEditButton(driveLog, driveLog.status === RecordedEntryStatus.Changeable &&
			driveLog.startPlatform !== Platform.TOUR_MOBILE && driveLog.endPlatform !== Platform.TOUR_MOBILE);
		this.updateBorder(driveLog.business);
	}

	protected setModalTitle(driveLog: ReportDriveLog) {
		const title = `<h4 class="no-margin">${htmlEscape(driveLog.plate)}</h4>
							<h6 class="text-grey no-margin">${htmlEscape(driveLog.carName)}</h6>`;

		this.dialog.setTitle(title);
	}

	/**
	 * Loads the history/revisions of the record
	 * Initializes the table containing this data if not already done
	 * @param {ReportDriveLog} driveLog
	 * @protected
	 */
	protected loadRevisionTable(driveLog: ReportDriveLog) {
		if (!this.driveLogRevisionTable) {
			this.driveLogRevisionTable = new DriveLogDetailsDialogRevisionTable({
				allowExpand: false,
				clampNote: false,
				element: this.dom.find("#driveLogDetailsDialogRevisionTable"),
				orderable: false,
				initialFilter: DriveLogDetailsDialogRevisionTable.buildFilterParams(driveLog.id)
			});
			this.driveLogRevisionTable.reload();
		} else {
			this.driveLogRevisionTable.filterByDriveLogId(driveLog.id);
		}
	}

	protected setDriveLogFields(driveLog: ReportDriveLog) {
		const businessValue = driveLog.business ?
			`<i class="fal fa-dollar-sign position-left"></i> ${messages["record.details.business"]}` : messages["record.details.private"];
		this.dom.find("#driveLogDetailsDialogBusiness").html(businessValue);
		this.dom.find("#driveLogDetailsDialogCar").text(driveLog.carName);
		this.dom.find("#driveLogDetailsDialogDuration").text(formatDuration(driveLog.durationNet));
		this.dom.find(".driveLogDetailsDialogStartMileage").text(driveLog.startMileage);
		this.dom.find(".driveLogDetailsDialogEndMileage").text(driveLog.endMileage);
		this.dom.find(".driveLogDetailsDialogDistance").text(driveLog.distance);

		this.setDriveLogRouteInfoFields(driveLog);
	}

	protected updateBorder(business: boolean) {
		const modalContent = this.dom.parents("div.modal-content");

		modalContent.toggleClass("border-BUSINESS", business);
		modalContent.toggleClass("border-PRIVATE", !business);
		modalContent.removeClass("border-NOT_INITIALIZED");
	}

	/**
	 * Toggles the the container if no custom fields or a route info fields are available
	 * @protected
	 */
	protected toggleAdvancedFieldsContainer(_: ReportDriveLog) {
		this.dom.find(".recordDetailsDialogAdvancedFieldsContainer").toggle(this.recordWithCustomFieldValues);
	}

	/**
	 * Removes the custom fields of the parent container
	 * These fields are appended dynamically
	 * @protected
	 */
	protected resetRecordDetailsDialogAdvancedFields() {
		this.dom.find("#recordRevisionTableCollapse").collapse("hide");
		this.dom.find(".customFieldFlexBox").remove();
	}

	private setDriveLogRouteInfoFields(driveLog: ReportDriveLog) {
		const purposeAvailable = driveLog.purpose !== "";
		const routeAvailable = driveLog.route !== "";
		const visitedAvailable = driveLog.visited !== "";

		this.dom.find(".driveLogDetailsNoPurposeBadge").toggle(!purposeAvailable);
		this.dom.find(".driveLogDetailsNoRouteBadge").toggle(!routeAvailable);
		this.dom.find(".driveLogDetailsNoVisitedBadge").toggle(!visitedAvailable);

		this.dom.find(".driveLogDetailsDialogPurpose > dd").text(driveLog.purpose)
			.closest("div").toggle(purposeAvailable);
		this.dom.find(".driveLogDetailsDialogVisited > dd").text(driveLog.visited)
			.closest("div").toggle(visitedAvailable);
		this.dom.find(".driveLogDetailsDialogRoute > dd").text(driveLog.route)
			.closest("div").toggle(routeAvailable);
	}

	protected setAddressAndLatLngFields(gpsPosition: GPSPosition, field: JQuery) {
		super.setAddressAndLatLngFields(gpsPosition, field);
		field.closest(".panel-body").find(".mileage-container")
			.css("margin-top", gpsPosition.formattedAddress ? "18px" : "10px");
	}

	protected getStatus(record: ReportDriveLog): RecordedEntryStatus {
		return record.status;
	}
}

export class DriveLogDetailsDialogRevisionTable extends BasicDataTable<ReportDriveLog> {

	constructor(public config: BasicDataTableConfig) {
		super("/timr/reports/getDriveLogRevisions.json", config);
	}

	public static buildFilterParams(driveLogId: number): RequestParam[] {
		return [{
			name: "id",
			value: driveLogId
		}];
	}

	public filterByDriveLogId(driveLogId: number) {
		return this.filter(DriveLogDetailsDialogRevisionTable.buildFilterParams(driveLogId));
	}

	protected getColumnSettings(): TypedColumnSettings<ReportDriveLog>[] {
		return [
			{data: "lastModified", orderable: this.config.orderable, render: BasicDataTable.renderDateTimeLocal, visible: true},
			{data: "modifiedByUser", orderable: false, render: BasicDataTable.renderText},
			{data: "description", orderable: false, render: (_data, _type, row) => DriveLogTable.renderDetails(row), className: "drive-log-details"},
			{
				data: "startPlatform",
				orderable: false,
				render: (d, t, r, m) => BasicDataTable.renderPlatformIcon(d, t, r, m, true),
				className: "slim-icon-column"
			},
			{
				data: "start",
				orderable: this.config.orderable,
				render: BasicDataTable.renderDateTimeWithTimeZone,
				className: "pl-10 pr-10 time-column"
			},
			{
				data: "endPlatform",
				orderable: false,
				render: (d, t, r, m) => BasicDataTable.renderPlatformIcon(d, t, r, m, false),
				className: "slim-icon-column"
			},
			{data: "durationNet", orderable: false, render: RecordingTable.renderDuration, className: "text-right"},
			{
				data: "startMileage",
				orderable: this.config.orderable,
				render: BasicDataTable.renderNumber,
				className: "text-right"
			},
			{data: "endMileage", orderable: this.config.orderable, render: BasicDataTable.renderNumber, className: "text-right"},
			{data: "distance", orderable: this.config.orderable, render: BasicDataTable.renderNumber, className: "text-right"},
			{
				data: "category",
				orderable: false,
				render: DriveLogTable.renderDriveLogCategory,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			},
			{
				data: "status",
				orderable: false,
				render: RecordingTable.renderStatus,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[5, "desc"]];
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			pageLength: 4,
			deferLoading: 0,
			dom: `rt`
		};
	}
}
